/**
 * Release 2 C4 — "Set rights" for a selection (`bulkSetAssetRights`). Merge (default) sends only the fields you
 * changed (an emptied field clears that key); replace sends the whole record. EDIT_METADATA is checked per asset:
 * the ones you cannot edit come back in `failed` and are reported, not hidden.
 */
import { useState } from 'react';
import { useBulkSetAssetRights, type AssetRights } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { plural } from '@/lib/format';
import { RightsFields, rightsBody, type RightsKey } from './RightsEditor';

export function BulkRightsDialog({ ids, onClose }: { ids: string[]; onClose: () => void }) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const bulk = useBulkSetAssetRights(wsId);
  const [draft, setDraft] = useState<AssetRights>({});
  const [touched, setTouched] = useState<Set<RightsKey>>(new Set());
  const [replace, setReplace] = useState(false);
  const submit = () => {
    const rights = replace ? rightsBody(draft) : rightsBody(draft, touched);
    bulk.mutate(
      { asset_ids: ids, rights, merge: !replace },
      {
        onSuccess: (r) => {
          const ok = r.succeeded.length;
          const failed = r.failed.length;
          toast({ kind: failed ? 'info' : 'success', title: `Rights set on ${plural(ok, 'asset', 'assets')}`, body: failed ? `${plural(failed, 'asset was', 'assets were')} skipped (${[...new Set(r.failed.map((f) => (f.code === 'permission_denied' ? 'no edit permission' : f.code)))].join(', ')})` : undefined });
          onClose();
        },
        onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not set rights' }),
      },
    );
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Set rights on ${plural(ids.length, 'asset', 'assets')}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={bulk.isPending || (!replace && touched.size === 0)} data-bulk-rights-apply>
            Apply
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm" data-bulk-rights>
        <fieldset className="flex gap-4 text-xs">
          <legend className="sr-only">How to apply</legend>
          <label className="flex items-center gap-1.5"><input type="radio" name="bulk-rights-mode" checked={!replace} onChange={() => setReplace(false)} /> Change only the fields I edit</label>
          <label className="flex items-center gap-1.5"><input type="radio" name="bulk-rights-mode" checked={replace} onChange={() => setReplace(true)} /> Replace the whole record</label>
        </fieldset>
        <RightsFields
          value={draft}
          touched={replace ? undefined : touched}
          onChange={(k, v) => {
            setDraft((d) => ({ ...d, [k]: v }));
            setTouched((t) => new Set(t).add(k));
          }}
        />
        <p className="text-xs text-fg-subtle">{replace ? 'Fields left empty are cleared on every selected asset.' : touched.size ? `Will change: ${[...touched].map((k) => k.replace(/_/g, ' ')).join(', ')}. An emptied field is cleared.` : 'Edit a field to change it on every selected asset.'}</p>
      </div>
    </Dialog>
  );
}
